import { Typography } from "@mui/material";
import { TextWithTooltip } from "components/Forms/TextWithTooltip";
import React, { useRef, useEffect } from "react";

interface CustomEditorProps {
  html: string;
  onChange?: (html: string) => void;
  label?: string | React.ReactNode;
  tooltip?: string;
}

const CustomEditor: React.FC<CustomEditorProps> = ({ html, onChange, label, tooltip }) => {
  const divRef = useRef<HTMLDivElement>(null);

  // Update the content when the html prop changes
  useEffect(() => {
    if (divRef.current && divRef.current.innerHTML !== html) {
      divRef.current.innerHTML = html;
    }
  }, [html]);

  // Handle user edits
  const handleInput = () => {
    if (divRef.current && onChange) {
      onChange(divRef.current.innerHTML);
    }
  };

  // Intercept Enter to insert <br> instead of new block
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const selection = document.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const br = document.createElement("br");
        range.deleteContents();
        range.insertNode(br);
        range.setStartAfter(br);
        range.setEndAfter(br);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            position: "absolute",
            top: -12,
            left: 12,
            px: 0.5,
            background: "#fff",
            zIndex: 1,
            fontSize: "0.85rem",
            pointerEvents: "none",
          }}
        >
          {tooltip ? <TextWithTooltip text={label} tooltip={tooltip} /> : label}
        </Typography>
      )}
      <div
        ref={divRef}
        contentEditable
        style={{
          minHeight: 120,
          border: "1px solid #ccc",
          borderRadius: 4,
          padding: 8,
          outline: "none",
          background: "#fff",
          lineHeight: 1.4,
        }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        suppressContentEditableWarning
        className="custom-editor-content"
      />
      <style>{`
        .custom-editor-content div, .custom-editor-content p {
          margin: 0;
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default CustomEditor;
